import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type Timezone from "@/lib/timezone/Timezone";
import { RefreshContextProvider } from "@/features/dashboard/features/refresh-timer/context-provider";
import TimeRange from "@/types/time-range";

/**
 * The controllable runtime a widget needs to behave "as if on a dashboard":
 * the selected time range and location (plus their setters) and the ambient
 * timezone. This is the single source of truth that both the dashboard page and
 * the CMS widget host provide, and that the `@/features/dashboard/store` read
 * hooks resolve from.
 *
 * Timezone lives here alongside the time range because both are seeded and
 * provided over the same boundary, so widgets read one ambient runtime.
 */
export interface WidgetRuntime {
  timeRange: TimeRange;
  /** Partial update; unspecified bounds keep their current value (store parity). */
  setTimeRange: (timeRange: { startHour?: number; endHour?: number }) => void;
  selectedLocationId?: string;
  setSelectedLocationId: (locationId: string) => void;
  /** Ambient dashboard timezone; `undefined` until resolved (or outside a provider). */
  timezone?: Timezone;
}

/**
 * Apply a partial time-range update, keeping any bound the caller omits. This is
 * the merge semantics the previous store used (`startHour ?? current`), extracted
 * as a pure function so it can be unit-tested without rendering.
 */
export const mergeTimeRange = (
  current: TimeRange,
  next: { startHour?: number; endHour?: number },
): TimeRange =>
  new TimeRange(next.startHour ?? current.hoursStart, next.endHour ?? current.hoursEnd);

const noop = () => {};

/**
 * Fallback used when a consumer renders without a provider. Mirrors the initial
 * state of the previous module-global store (a default {@link TimeRange}, no
 * location) with no-op setters, so reads never throw and stray writes are
 * ignored rather than crashing.
 */
export const DEFAULT_RUNTIME: WidgetRuntime = {
  timeRange: new TimeRange(),
  selectedLocationId: undefined,
  setTimeRange: noop,
  setSelectedLocationId: noop,
  timezone: undefined,
};

export const WidgetRuntimeContext = createContext<WidgetRuntime>(DEFAULT_RUNTIME);

/** Read the nearest widget runtime (or the non-throwing default outside a provider). */
export const useWidgetRuntime = (): WidgetRuntime => useContext(WidgetRuntimeContext);

/** Read the ambient dashboard timezone (or `undefined` outside a provider / until resolved). */
export const useWidgetTimezone = (): Timezone | undefined => useWidgetRuntime().timezone;

interface WidgetRuntimeProviderProps {
  /**
   * Seeds the initial time range and re-seeds whenever its bounds change (e.g.
   * switching dashboards). Falls back to a default {@link TimeRange}.
   */
  initialTimeRange?: TimeRange;
  initialSelectedLocationId?: string;
  /** Ambient timezone shared with hosted widgets (charts, tables, map controls). */
  timezone?: Timezone;
  /** Auto-refresh interval (ms) for the refresh machine wrapped around the runtime. */
  autoRefreshInterval: number;
}

/**
 * Establishes the full runtime environment a widget needs to behave "as if on a
 * dashboard": the refresh machine ({@link RefreshContextProvider}) wrapping the
 * widget runtime (selected time range / location + ambient timezone). Both the
 * dashboard page and the CMS widget host mount this directly.
 */
export const WidgetRuntimeProvider = ({
  initialTimeRange,
  initialSelectedLocationId,
  timezone,
  autoRefreshInterval,
  children,
}: PropsWithChildren<WidgetRuntimeProviderProps>) => {
  const [timeRange, setTimeRangeState] = useState<TimeRange>(
    () => initialTimeRange ?? new TimeRange(),
  );
  const [selectedLocationId, setSelectedLocationIdState] = useState<string | undefined>(
    initialSelectedLocationId,
  );

  // Re-seed when the configured defaults change (e.g. a different dashboard).
  // Keyed on the primitive bounds so a fresh-but-equal TimeRange object on each
  // parent render does not clobber the user's picks.
  const seedStart = initialTimeRange?.hoursStart;
  const seedEnd = initialTimeRange?.hoursEnd;
  useEffect(() => {
    if (seedStart === undefined || seedEnd === undefined) return;
    setTimeRangeState(new TimeRange(seedStart, seedEnd));
  }, [seedStart, seedEnd]);

  const setTimeRange = useCallback((next: { startHour?: number; endHour?: number }) => {
    setTimeRangeState((current) => mergeTimeRange(current, next));
  }, []);

  const setSelectedLocationId = useCallback((locationId: string) => {
    setSelectedLocationIdState(locationId);
  }, []);

  const value = useMemo<WidgetRuntime>(
    () => ({
      timeRange,
      selectedLocationId,
      setTimeRange,
      setSelectedLocationId,
      timezone,
    }),
    [timeRange, selectedLocationId, setTimeRange, setSelectedLocationId, timezone],
  );

  return (
    <RefreshContextProvider autoRefreshInterval={autoRefreshInterval}>
      <WidgetRuntimeContext.Provider value={value}>
        {children}
      </WidgetRuntimeContext.Provider>
    </RefreshContextProvider>
  );
};
